<script setup lang="ts">
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/vue';
import {
  PopoverButton,
  PopoverPanel,
  Popover as HeadlessUiPopover,
} from '@headlessui/vue';
import { computed, ref } from 'vue';
import type { PopoverProps } from './popover';
import { FLOATING_OVERLAY_Z_INDEX } from '../layering/layering';

const props = withDefaults(defineProps<PopoverProps>(), {
  placement: 'bottom',
});

const POPOVER_GAP = 12;
const VIEWPORT_PADDING = 16;
const MIN_AVAILABLE_SIZE = 0;

const triggerRef = ref<HTMLElement | null>(null);
const popoverRef = ref<HTMLElement | null>(null);

const positioningMiddleware = computed(() => {
  if (!props.overlapTrigger) {
    return [offset(POPOVER_GAP), flip(), shift({ padding: VIEWPORT_PADDING })];
  }

  return [
    offset(({ rects }) => -rects.reference.height),
    size({
      padding: VIEWPORT_PADDING,
      apply({ availableHeight, availableWidth, elements }): void {
        const constrainedHeight = Math.max(availableHeight, MIN_AVAILABLE_SIZE);
        const constrainedWidth = Math.max(availableWidth, MIN_AVAILABLE_SIZE);

        elements.floating.style.setProperty(
          '--popover-available-height',
          `${String(constrainedHeight)}px`,
        );
        elements.floating.style.maxHeight = `${String(constrainedHeight)}px`;
        elements.floating.style.maxWidth = `${String(constrainedWidth)}px`;
      },
    }),
    shift({ padding: VIEWPORT_PADDING }),
  ];
});

const { floatingStyles } = useFloating(triggerRef, popoverRef, {
  placement: () => props.placement,
  middleware: positioningMiddleware,
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
});
</script>

<template>
  <HeadlessUiPopover class="w-full min-w-0">
    <PopoverButton ref="triggerRef" class="w-full min-w-0">
      <slot name="trigger" />
    </PopoverButton>

    <Teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <PopoverPanel
          v-slot="{ close }"
          ref="popoverRef"
          class="fixed rounded-lg bg-white shadow-lg"
          :class="[
            props.noPadding ? 'p-0' : 'p-4',
            props.overlapTrigger ? 'overflow-x-hidden overflow-y-auto' : '',
          ]"
          :style="{ ...floatingStyles, zIndex: FLOATING_OVERLAY_Z_INDEX }"
        >
          <slot name="content" :close="close" />
        </PopoverPanel>
      </transition>
    </Teleport>
  </HeadlessUiPopover>
</template>
